import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { v4 as uuidv4 } from 'uuid';
import { isPrerenderCrawl } from './isPrerenderCrawl';
import { getAnalyticsMode, PRIVACY_EVENT, recordGameCompleted } from './privacy';
import { createCountryPlugin, fetchCountry } from './countryEnrichment';

const AMPLITUDE_API_KEY = 'e1c9f3a8e59921d784f9145ac3178f84';
const AMPLITUDE_API_KEY_DEV = '7818168e1c8243b8e742985b02dd714b';
const STORAGE_KEY = 'topamine_user_id';
const TRAFFIC_SOURCE_KEY = 'topamine_traffic_source';

let isInitialized = false;
let everInitialized = false;
let currentMode = null;
// One /api/geo lookup per page load, shared by every event (see getCountryPromise).
let countryPromise = null;

// Get or create user ID
const getUserId = () => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return null;
    }
    let userId = localStorage.getItem(STORAGE_KEY);
    if (!userId) {
        userId = uuidv4();
        localStorage.setItem(STORAGE_KEY, userId);
    }
    return userId;
};

// Check if we're running in a test environment (Playwright, Cypress, etc.)
const isTestEnvironment = () => {
    if (typeof window === 'undefined') return false;

    // Check for Playwright/Puppeteer
    if (window.navigator.webdriver) return true;

    // Check for common test environment indicators
    if (window.__playwright || window.__puppeteer) return true;

    // Check for Cypress
    if (window.Cypress) return true;

    return false;
};

const isDev = () => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return (
        process.env.NODE_ENV === 'development' ||
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.includes('netlify.app')
    );
};

// Extract URL parameters for traffic source tracking
const getUrlParams = () => {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const source = params.get('source');

    const result = {};
    if (ref) result.ref = ref;
    if (source) result.source = source;

    return result;
};

// Get or store traffic source parameters
const getTrafficSource = () => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return null;
    }

    // Check for URL parameters first
    const urlParams = getUrlParams();

    // If we have URL parameters, store them and return
    if (Object.keys(urlParams).length > 0) {
        const trafficData = {
            ...urlParams,
            firstSeen: new Date().toISOString(),
            landingPage: window.location.pathname
        };
        localStorage.setItem(TRAFFIC_SOURCE_KEY, JSON.stringify(trafficData));
        return trafficData;
    }

    // Otherwise, return stored traffic source if it exists
    const stored = localStorage.getItem(TRAFFIC_SOURCE_KEY);
    return stored ? JSON.parse(stored) : null;
};

// Starts the persistent-ID parts of analytics: user ID, traffic-source
// attribution, and session replay. Only ever called in 'full' mode.
const startFullTracking = (userId, { alreadyInitialized } = {}) => {
    if (alreadyInitialized) {
        // Upgrading an anonymous page view to a full one after the age prompt.
        amplitude.setUserId(userId);
        amplitude.setDeviceId(userId);
    }

    amplitude.add(sessionReplayPlugin({
        sampleRate: 1, // 100% sampling - adjust as needed
    }));

    const trafficSource = getTrafficSource();
    if (trafficSource) {
        const identify = new amplitude.Identify();
        if (trafficSource.ref) identify.set('initial_ref', trafficSource.ref);
        if (trafficSource.source) identify.set('initial_source', trafficSource.source);
        if (trafficSource.landingPage) identify.set('landing_page', trafficSource.landingPage);
        if (trafficSource.firstSeen) identify.set('first_seen', trafficSource.firstSeen);
        safeIdentify(identify);

        safeTrack('traffic_source_captured', {
            ref: trafficSource.ref || null,
            source: trafficSource.source || null,
            landing_page: trafficSource.landingPage || null,
            first_seen: trafficSource.firstSeen || null
        });
    }
};

// Initialize Amplitude (lazy initialization). See privacy.js for the modes.
const initializeAmplitude = () => {
    // Skip if already initialized
    if (isInitialized) {
        return;
    }

    // Skip if in test environment (Playwright, Cypress, etc.)
    if (isTestEnvironment()) {
        console.log('Skipping Amplitude initialization in test environment');
        return;
    }

    // Only initialize in browser environment (not during SSR/react-snap)
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return;
    }

    // Check if we're in react-snap. This never actually matched: react-snap
    // sets navigator.userAgent to the literal string "ReactSnap", not
    // anything containing "HeadlessChrome" — so every prerender crawl has
    // been initializing Amplitude (and firing session-replay + whatever
    // events happen to run during the crawl) this whole time. See
    // isPrerenderCrawl.js.
    if (isPrerenderCrawl()) {
        console.log('Skipping Amplitude initialization in react-snap');
        return;
    }

    const mode = getAnalyticsMode();
    if (mode === 'off') {
        return;
    }

    try {
        const devMode = isDev();
        const apiKey = devMode ? AMPLITUDE_API_KEY_DEV : AMPLITUDE_API_KEY;
        const full = mode === 'full';

        const options = {
            defaultTracking: {
                sessions: true,
                pageViews: true,
                formInteractions: false,
                fileDownloads: false,
            },
            // Don't store IPs or derive location from them; country is
            // attached separately from a server-side lookup (see setCountry).
            trackingOptions: {
                ipAddress: false,
            },
        };

        if (full && !getUserId()) return;

        // Registered before init so the SDK runs it ahead of its own page
        // view and session tracking: the first events get the country too.
        // Adding it again on a later call is a no-op (plugins dedupe by name).
        amplitude.add(createCountryPlugin(getCountryPromise()));

        if (full) {
            const userId = getUserId();
            amplitude.init(apiKey, userId, { ...options, attribution: { disabled: false } });
        } else {
            // Anonymous baseline: nothing identifying is persisted. The device
            // ID is a throwaway random value for this page load only.
            amplitude.init(apiKey, undefined, {
                ...options,
                attribution: { disabled: true },
                identityStorage: 'none',
                deviceId: uuidv4(),
            });
        }

        // Must be set before anything below calls safeTrack/safeIdentify.
        isInitialized = true;
        everInitialized = true;
        currentMode = mode;

        if (full) {
            startFullTracking(null);
        } else {
            // Which campaign sent them, from this page's URL only (not stored).
            const { ref, source } = getUrlParams();
            if (ref || source) {
                safeTrack('traffic_source_captured', {
                    ref: ref || null,
                    source: source || null,
                    landing_page: window.location.pathname,
                });
            }
        }

        console.log(`Amplitude initialized successfully (${mode}) - ${isDev() ? 'Dev' : ''}`);
    } catch (error) {
        console.error('Failed to initialize Amplitude:', error);
    }
};

// Every call site funnels through these so nothing is ever sent unless
// Amplitude is initialized and the visitor hasn't turned analytics off.
const safeTrack = (...args) => {
    if (isInitialized && getAnalyticsMode() !== 'off') amplitude.track(...args);
};
const safeIdentify = (...args) => {
    if (isInitialized && getAnalyticsMode() !== 'off') amplitude.identify(...args);
};

// Country only, from Netlify's geo header via /api/geo. The IP itself is
// neither stored by Amplitude (ipAddress: false) nor by us. The plugin in
// countryEnrichment.js puts it on every event, including the first ones.
const getCountryPromise = () => {
    if (!countryPromise) countryPromise = fetchCountry();
    return countryPromise;
};

// React to the age prompt / privacy page changes at runtime.
if (typeof window !== 'undefined') {
    window.addEventListener(PRIVACY_EVENT, () => {
        const mode = getAnalyticsMode();

        if (mode === 'off') {
            if (isInitialized) {
                amplitude.setOptOut(true);
                isInitialized = false;
            }
            return;
        }

        if (!everInitialized) {
            initializeAmplitude();
            return;
        }

        if (mode === 'full' && currentMode === 'baseline') {
            // Anonymous page view upgraded after the age prompt.
            const userId = getUserId();
            if (!userId) return;
            amplitude.setOptOut(false);
            isInitialized = true;
            currentMode = 'full';
            startFullTracking(userId, { alreadyInitialized: true });
        } else if (mode === 'baseline' && currentMode === 'full') {
            // Stepped down from full (declined later). Replay can't be
            // detached cleanly mid-page, so stay silent until the next page
            // load, which starts in baseline.
            amplitude.setOptOut(true);
            isInitialized = false;
        } else {
            // Same mode as before, just coming back from 'off'.
            amplitude.setOptOut(false);
            isInitialized = true;
        }
    });
}

// Defer Amplitude init until after page load to avoid INP impact
if (typeof window !== 'undefined') {
    if (document.readyState === 'complete') {
        initializeAmplitude();
    } else {
        window.addEventListener('load', initializeAmplitude, { once: true });
    }
}

// Helper to ensure Amplitude is initialized before tracking
const ensureInitialized = () => {
    if (!isInitialized) {
        initializeAmplitude();
    }
};

// Utility class for tracking game events
const AmplitudeTracker = {
    // Track when game starts
    trackGameStart: (wordLength, topic, wordId, wordMeta = {}) => {
        try {
            ensureInitialized();
            safeTrack("game_started", {
                wordLength: wordLength,
                topic: topic,
                wordId: wordId,
                ...wordMeta,
                startTime: Date.now()
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track each word guess
    trackWordGuess: (word, attemptNumber, isCorrect) => {
        try {
            ensureInitialized();
            safeTrack("word_guessed", {
                word: word,
                attempt: attemptNumber,
                correct: isCorrect
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track when game ends
    trackGameComplete: (result, attempts, wordId, word, topic, wordMeta = {}) => {
        recordGameCompleted();
        try {
            ensureInitialized();
            safeTrack("game_completed", {
                result: result,
                attempts: attempts,
                wordId: wordId,
                word: word,
                word_length: word.length,
                topic: topic,
                ...wordMeta,
                endTime: Date.now()
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track share button clicked
    trackShareButtonClicked: (topic) => {
        try {
            ensureInitialized();
            safeTrack("share_button_clicked", { topic: topic });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track challenge friend button clicked (pre-game)
    trackChallengeFriendClicked: (topic, withProgress) => {
        try {
            ensureInitialized();
            safeTrack("challenge_friend", { topic: topic, with_progress: withProgress });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track sharing
    trackShare: (platform, topic, withProgress) => {
        try {
            ensureInitialized();
            safeTrack("shared", { platform: platform, topic: topic, with_progress: withProgress });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track Play again from modal
    trackPlayAgain: (topic) => {
        try {
            ensureInitialized();
            safeTrack("play_again", { topic: topic });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track Play again from immediate buttons (before modal appears)
    trackPlayAgainImmediate: (topic) => {
        try {
            ensureInitialized();
            safeTrack("play_again_immediate", { topic: topic });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track topic selection
    trackTopicSelection: (topic) => {
        try {
            ensureInitialized();
            safeTrack("topic_selected", { topic: topic });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track tutorial views
    trackTutorialView: () => {
        try {
            ensureInitialized();
            safeTrack("tutorial_viewed");
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track when rules are opened
    trackRulesView: () => {
        try {
            ensureInitialized();
            safeTrack("rules_viewed");
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track a "play daily challenge" CTA click. surface identifies which
    // entry point was used (e.g. 'topic_landing', 'end_game_modal', 'home_hero',
    // 'navbar_streak_nudge', 'navbar_convert_nudge', 'countdown_widget') so we can compare prominence.
    trackDailyChallengeCTA: (surface, topic) => {
        try {
            ensureInitialized();
            safeTrack("daily_challenge_cta_clicked", {
                surface: surface,
                topic: topic || null
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track daily challenge started
    trackDailyChallengeStart: (topic, challengeNumber, wordLength, prefillCount) => {
        try {
            ensureInitialized();
            safeTrack("daily_challenge_started", {
                topic: topic,
                challengeNumber: challengeNumber,
                wordLength: wordLength,
                prefillCount: prefillCount,
                availableAttempts: wordLength + 1 - prefillCount,
                startTime: Date.now()
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track daily challenge guess
    trackDailyGuess: (topic, challengeNumber, attemptNumber, isCorrect) => {
        try {
            ensureInitialized();
            safeTrack("daily_word_guessed", {
                topic: topic,
                challengeNumber: challengeNumber,
                attempt: attemptNumber,
                correct: isCorrect
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track daily challenge completed
    trackDailyChallengeComplete: (result, attempts, topic, challengeNumber, word, prefillCount) => {
        recordGameCompleted();
        try {
            ensureInitialized();
            safeTrack("daily_challenge_completed", {
                result: result,
                attempts: attempts,
                topic: topic,
                challengeNumber: challengeNumber,
                word: word,
                wordLength: word.length,
                prefillCount: prefillCount,
                availableAttempts: word.length + 1 - prefillCount,
                endTime: Date.now()
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track daily topic change
    trackDailyTopicChange: (fromTopic, toTopic) => {
        try {
            ensureInitialized();
            safeTrack("daily_topic_changed", {
                from_topic: fromTopic,
                to_topic: toTopic
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track daily share modal opened
    trackDailyShareModalOpened: (topic, challengeNumber, isWin) => {
        try {
            ensureInitialized();
            safeTrack("daily_share_modal_opened", {
                topic: topic,
                challengeNumber: challengeNumber,
                result: isWin ? 'win' : 'loss'
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    trackDailyShare: (platform, topic, challengeNumber, isWin) => {
        try {
            ensureInitialized();
            safeTrack("daily_challenge_shared", {
                platform: platform,
                topic: topic,
                challengeNumber: challengeNumber,
                result: isWin ? 'win' : 'loss'
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track search queries that returned no results — feeds topic pipeline
    trackTopicSearchNoResults: (query, currentTopic) => {
        try {
            ensureInitialized();
            safeTrack("topic_search_no_results", {
                query: query,
                current_topic: currentTopic
            });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track feedback submission
    trackFeedbackSubmitted: (rating, hasMessage) => {
        try {
            ensureInitialized();
            safeTrack("feedback_submitted", { rating, has_message: hasMessage });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track a streak change. Pure streaks.js stays analytics-free; call sites
    // pass the prev/next objects and this derives the action + skips no-ops.
    // type: 'daily' | 'play' | 'win'. Date streaks use currentStreak/bestStreak/
    // freezeTokens; win streak uses current/best.
    trackStreak: (type, prev, next) => {
        try {
            const prevCur = prev.currentStreak ?? prev.current ?? 0;
            const nextCur = next.currentStreak ?? next.current ?? 0;
            const nextBest = next.bestStreak ?? next.best ?? 0;
            const prevFreeze = prev.freezeTokens ?? 0;
            const nextFreeze = next.freezeTokens ?? 0;

            let action;
            if (nextCur > prevCur) action = prevCur === 0 ? 'started' : 'extended';
            else if (nextCur < prevCur) action = 'broken';
            else if (nextFreeze < prevFreeze) action = 'frozen';
            else return; // no meaningful change (e.g. same-day no-op)

            ensureInitialized();

            const MILESTONES = [3, 7, 14, 30, 60, 100];
            safeTrack("streak_updated", {
                streak_type: type,
                action: action,
                current_streak: nextCur,
                previous_streak: prevCur,
                best_streak: nextBest,
                freeze_tokens: nextFreeze,
                is_milestone: action === 'extended' && MILESTONES.includes(nextCur),
            });

            // User properties → segment any event by streak length (retention cohorts).
            const identify = new amplitude.Identify();
            identify.set(`${type}_streak_current`, nextCur);
            identify.set(`${type}_streak_best`, nextBest);
            if (type === 'daily') identify.set('freeze_tokens', nextFreeze);
            safeIdentify(identify);
        } catch (error) {
            console.error('Analytics error:', error);
        }
    },

    // Track errors
    trackError: (errorType, errorMessage) => {
        try {
            ensureInitialized();
            safeTrack("error_occurred", { errorType, errorMessage });
        } catch (error) {
            console.error('Analytics error:', error);
        }
    }
};

export default AmplitudeTracker;
